import CustomFormCanvas from '@chiroup/components/customForm/CustomFormCanvas';
import {
  isElementVisible,
  unfilledRequiredFieldIds,
} from '@chiroup/core/functions/CustomForm.helpers';
import { PatientForm } from '@chiroup/core/types/PatientForm.type';
import { useWindowDimensions } from '@chiroup/hooks/useWindowDimensions';
import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo, useState } from 'react';
import requestService from '../../services/request.service';
import { customFormRenderWidth } from './customFormRenderWidth';

type Props = {
  requestId: string;
  patientFormId?: string;
  form?: PatientForm;
  basePdfUrl?: string;
  validationCode: string | number;
  prMap?: { [key: string]: string };
  onComplete: () => void;
  alreadyComplete?: boolean;
  kiosk?: boolean;
  primaryColor?: string;
};

const StepCustomForm: React.FC<Props> = ({
  requestId,
  patientFormId,
  form,
  basePdfUrl,
  validationCode,
  prMap,
  onComplete,
  alreadyComplete,
  kiosk,
  primaryColor,
}) => {
  const { width } = useWindowDimensions();
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(form?.fieldValues || {});
  const [signatures, setSignatures] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const setValue = (id: string, val: string) => setFieldValues(v => ({ ...v, [id]: val }));

  const setSignature = (id: string, dataUrl: string) =>
    setSignatures(s => {
      const next = { ...s };
      if (dataUrl) next[id] = dataUrl;
      else delete next[id];
      return next;
    });

  const elements = useMemo(() => form?.elements || [], [form?.elements]);

  // Conditional "drop": when a field becomes hidden, clear what was entered so it
  // never reaches the submitted form (the chosen behavior).
  useEffect(() => {
    const hidden = elements.filter(e => e.visibleWhen && !isElementVisible(e, fieldValues));
    const clearFv = hidden.filter(e => fieldValues[e.id] !== undefined);
    const clearSig = hidden.filter(e => e.type === 'signature' && signatures[e.id] !== undefined);
    if (clearFv.length) {
      setFieldValues(v => {
        const next = { ...v };
        clearFv.forEach(e => delete next[e.id]);
        return next;
      });
    }
    if (clearSig.length) {
      setSignatures(s => {
        const next = { ...s };
        clearSig.forEach(e => delete next[e.id]);
        return next;
      });
    }
  }, [elements, fieldValues, signatures]);

  // Required validation across ALL pages. Signatures live in a separate map here,
  // so merge them in — the shared helper keys signatures by element id.
  const requiredUnfilled = useMemo(
    () => unfilledRequiredFieldIds(elements, { ...fieldValues, ...signatures }).length > 0,
    [elements, fieldValues, signatures]
  );

  // An overlay form whose signed base-PDF URL failed to generate can't be filled.
  const overlayUnavailable = form?.mode === 'pdf-overlay' && !basePdfUrl;
  const renderWidth = customFormRenderWidth(width);

  const handleSubmit = async () => {
    if (!patientFormId || submitting) return;
    setSubmitting(true);
    try {
      await requestService.submitForm({
        requestId,
        patientFormId,
        fieldValues,
        signatures,
        validationCode,
        prMap,
        kiosk,
      });
      onComplete();
    } catch (err) {
      Sentry.captureException(err);
      setSubmitting(false);
    }
  };

  if (!form || !patientFormId) {
    return (
      <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
        Form unavailable.
      </div>
    );
  }

  if (alreadyComplete) {
    return (
      <div className="flex flex-col items-center gap-4 py-8 text-center">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {form.displayName || form.name}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          This form has already been completed.
        </p>
        <button
          type="button"
          onClick={onComplete}
          style={primaryColor ? { backgroundColor: primaryColor } : undefined}
          className="rounded-md bg-primary-600 px-6 py-2 text-sm font-semibold text-white shadow-sm"
        >
          Continue
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 py-4">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {form.displayName || form.name}
        </h2>
        {form.subtitle ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{form.subtitle}</p>
        ) : null}
      </div>

      <CustomFormCanvas
        elements={elements}
        mode={form.mode}
        basePdfUrl={basePdfUrl}
        pageCount={form.pageCount}
        renderWidth={renderWidth}
        fieldValues={fieldValues}
        signatures={signatures}
        onChange={setValue}
        onSignatureChange={setSignature}
        onCaptureError={err => Sentry.captureException(err)}
        applyHideIfTokenMissing
        primaryColor={primaryColor}
        lockUserOwnedFields
      />

      {requiredUnfilled && (
        <p className="text-xs text-amber-600">
          Please complete all required fields and signatures.
        </p>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting || requiredUnfilled || overlayUnavailable}
        style={primaryColor ? { backgroundColor: primaryColor } : undefined}
        className="rounded-md bg-primary-600 px-6 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting ? 'Submitting…' : 'Submit'}
      </button>
    </div>
  );
};

export default StepCustomForm;
